/**
 * LiveNotificationToast
 *
 * Dynamic-island-style top-of-screen pill that surfaces the most recent
 * in-app notification the moment it arrives. Auto-hides after 4s; tap
 * navigates to the notification's `url` (or /notifications if missing).
 *
 * Lives in the global app shell (mounted in App.jsx) so it overlays
 * every authenticated route. Hidden when no fresh notification is
 * pending or when the pilot isn't logged in.
 *
 * Polls /api/notifications every 30s. Tracks the latest seen id in a
 * ref so re-renders never replay a stale alert. On a fresh notif we
 * also vibrate a soft tap (via our Haptic helper) so mobile pilots
 * feel the alert land even with the volume muted.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, ChevronRight, X } from "lucide-react";
import api from "../lib/api";
import { Haptic } from "../lib/haptics";

const POLL_MS = 30000;
const AUTO_HIDE_MS = 4500;
// Routes where the toast should stay hidden — auth/onboarding pages
// where extra UI clutter hurts conversion, and the play surface where
// the canvas already maxes the screen.
const HIDDEN_PATHS = ["/login", "/register", "/forgot-password"];
function shouldHideOnPath(pathname) {
  if (!pathname) return false;
  return HIDDEN_PATHS.includes(pathname) || pathname.startsWith("/play/");
}

// Tone per notification kind — matches the bell drawer's existing
// color language so the toast feels native.
const KIND_TONE = {
  "friend-beat": "#ff007f",
  "friend-challenge": "#3aa8ff",
  achievement: "#ffd700",
  "daily-seed": "#00ffff",
  "weekly-featured": "#b026ff",
  "referral-bonus": "#00ff41",
  "tournament-ai-filled": "#3aa8ff",
  "streak-reminder": "#ff8c1a",
};

export default function LiveNotificationToast() {
  const [active, setActive] = useState(null); // { id, kind, title, body, url, tone }
  const lastSeenIdRef = useRef(null);
  const initializedRef = useRef(false);
  const dismissTimerRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const r = await api.get("/notifications");
        if (cancelled) return;
        const items = r.data?.items || [];
        if (!items.length) {
          if (!initializedRef.current) initializedRef.current = true;
          return;
        }
        const top = items[0];
        // First poll after mount — just record current latest so we
        // never replay backlog notifications as "live" toasts.
        if (!initializedRef.current) {
          lastSeenIdRef.current = top.id;
          initializedRef.current = true;
          return;
        }
        // No change since last poll — nothing to surface.
        if (top.id === lastSeenIdRef.current) return;
        // Already read = pilot opened the bell. Don't repeat-toast.
        if (top.read) {
          lastSeenIdRef.current = top.id;
          return;
        }
        lastSeenIdRef.current = top.id;
        setActive({
          id: top.id,
          kind: top.kind,
          title: top.title || "Notification",
          body: top.body || "",
          url: top.url || "/notifications",
          tone: KIND_TONE[top.kind] || "#00ffff",
        });
        Haptic.tap();
      } catch (_) {
        // Unauthed or network blip — ignore.
      }
    }

    poll();
    const intervalId = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // Auto-dismiss timer — restarts whenever a new notification becomes
  // active. Cleared on manual dismiss or tap-through so we don't fire
  // the hide animation after the pilot is already gone.
  useEffect(() => {
    if (!active) return undefined;
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = setTimeout(() => {
      setActive(null);
    }, AUTO_HIDE_MS);
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [active]);

  // Hide on routes that don't need this surface (login, /play game).
  // Polling continues so when the pilot leaves /play the latest miss
  // surfaces immediately on the next route.
  if (!active || shouldHideOnPath(pathname)) return null;

  const handleTap = async () => {
    // Best-effort read — fire-and-forget, the bell drawer will reflect it.
    try { api.post(`/notifications/${active.id}/read`); } catch (_) {}
    const target = active.url;
    setActive(null);
    if (target && target.startsWith("http")) {
      window.open(target, "_blank", "noopener");
    } else if (target) {
      navigate(target);
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setActive(null);
  };

  return (
    <div
      className="fixed top-[68px] left-1/2 -translate-x-1/2 z-[90] w-[min(92%,28rem)] pointer-events-none"
      data-testid="live-notification-toast-root"
    >
      <button
        type="button"
        onClick={handleTap}
        className="relative pointer-events-auto w-full glass border flex items-center gap-3 px-3 py-2.5 hover:translate-y-[1px] transition active:scale-[0.99] live-notif-pop"
        style={{
          borderColor: `${active.tone}80`,
          boxShadow: `0 4px 24px ${active.tone}33, 0 0 18px ${active.tone}22 inset`,
        }}
        data-testid="live-notification-toast"
      >
        <span
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center border"
          style={{ borderColor: `${active.tone}AA`, color: active.tone }}
          aria-hidden="true"
        >
          <Bell className="w-4 h-4" />
        </span>
        <span className="flex-1 min-w-0 text-left">
          <span
            className="block font-heading uppercase text-sm tracking-tight text-white truncate"
            data-testid="live-notif-title"
          >
            {active.title}
          </span>
          {active.body && (
            <span className="block text-white/65 text-[11px] sm:text-xs truncate">
              {active.body}
            </span>
          )}
        </span>
        <span
          className="flex-shrink-0 inline-flex items-center"
          style={{ color: active.tone }}
        >
          <ChevronRight className="w-4 h-4" />
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={handleDismiss}
          onKeyDown={(e) => e.key === "Enter" && handleDismiss(e)}
          className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-black border border-white/25 text-white/70 hover:text-white hover:border-white"
          data-testid="live-notification-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3 h-3" />
        </span>
      </button>
    </div>
  );
}
